import { useCallback, useEffect, useState } from 'react';
import {
  EngineError,
  disconnectCalendar,
  getAvailability,
  getCalendarConnection,
  putAvailability,
  startCalendarConnect,
  type CalendarConnection,
  getZoomOwnConnection,
  startZoomOwnConnect,
  disconnectZoomOwn,
} from '../../api/engineDashboard';
import type { Caps } from './DashboardApp';
import type { AvailabilityException, AvailabilityRule } from '../../types/dashboard';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const DEFAULT_RULE = (weekday: number): AvailabilityRule => ({
  weekday,
  start_time: '09:00',
  end_time: '17:00',
  // The mediator's OWN timezone — availability hours mean their local
  // wall-clock (hardcoded Denver mis-scheduled non-Denver mediators,
  // 2026-08-13).
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver',
  buffer_minutes: 15,
  max_sessions_per_day: 4,
});

interface Props {
  token: string;
  caps: Caps;
  onUnauthorized: () => void;
}

/**
 * Simple weekly availability grid: one optional window per weekday plus
 * date exceptions. Saves with full-replace semantics (PUT), matching the
 * Engine's availability endpoint.
 */
export function AvailabilityPage({ token, caps, onUnauthorized }: Props) {
  const [rules, setRules] = useState<AvailabilityRule[] | null>(null);
  const [exceptions, setExceptions] = useState<AvailabilityException[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const editable = !caps.readOnly;

  useEffect(() => {
    let cancelled = false;
    getAvailability(token)
      .then((res) => {
        if (cancelled) return;
        setRules(res.rules);
        setExceptions(res.exceptions);
      })
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) {
          onUnauthorized();
          return;
        }
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load availability');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  if (error && !rules) {
    return <div className="card p-6 text-center text-sm text-red-600">{error}</div>;
  }
  if (!rules) {
    return <div className="p-8 text-center text-sm text-gray-500">Loading availability…</div>;
  }

  const ruleFor = (weekday: number) => rules.find((r) => r.weekday === weekday);

  const toggleDay = (weekday: number) => {
    setRules((prev) => {
      const current = prev ?? [];
      return current.some((r) => r.weekday === weekday)
        ? current.filter((r) => r.weekday !== weekday)
        : [...current, DEFAULT_RULE(weekday)].sort((a, b) => a.weekday - b.weekday);
    });
  };

  const patchRule = (weekday: number, patch: Partial<AvailabilityRule>) => {
    setRules((prev) => (prev ?? []).map((r) => (r.weekday === weekday ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await putAvailability(token, { rules, exceptions });
      setSavedAt(Date.now());
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return;
      }
      setError(
        err instanceof EngineError && err.status === 403
          ? 'Your account cannot edit availability.'
          : err instanceof Error
            ? err.message
            : 'Save failed',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <section className="card p-4">
        <div className="flex items-center gap-2">
          <div className="section-label">Weekly availability</div>
          {caps.readOnly && (
            <span className="text-xs text-amber-700">Read-only account — viewing only</span>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {WEEKDAYS.map((name, weekday) => {
            const rule = ruleFor(weekday);
            return (
              <div key={weekday} className="flex flex-wrap items-center gap-2 text-sm">
                <label className="flex w-28 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(rule)}
                    disabled={!editable}
                    onChange={() => toggleDay(weekday)}
                  />
                  <span className={rule ? 'font-medium' : 'text-gray-400'}>{name}</span>
                </label>
                {rule && (
                  <>
                    <input
                      type="time"
                      className="rounded-md border border-surface-border px-2 py-1 text-xs"
                      value={rule.start_time}
                      disabled={!editable}
                      onChange={(e) => patchRule(weekday, { start_time: e.target.value })}
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="time"
                      className="rounded-md border border-surface-border px-2 py-1 text-xs"
                      value={rule.end_time}
                      disabled={!editable}
                      onChange={(e) => patchRule(weekday, { end_time: e.target.value })}
                    />
                    <label className="ml-2 flex items-center gap-1 text-xs text-gray-500">
                      buffer
                      <input
                        type="number"
                        min={0}
                        max={120}
                        className="w-14 rounded-md border border-surface-border px-1.5 py-1"
                        value={rule.buffer_minutes}
                        disabled={!editable}
                        onChange={(e) =>
                          patchRule(weekday, { buffer_minutes: Number(e.target.value) || 0 })
                        }
                      />
                      min
                    </label>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      max
                      <input
                        type="number"
                        min={1}
                        max={12}
                        className="w-12 rounded-md border border-surface-border px-1.5 py-1"
                        value={rule.max_sessions_per_day}
                        disabled={!editable}
                        onChange={(e) =>
                          patchRule(weekday, { max_sessions_per_day: Number(e.target.value) || 1 })
                        }
                      />
                      /day
                    </label>
                  </>
                )}
              </div>
            );
          })}
        </div>
        {rules.length > 0 && (
          <div className="mt-3 text-xs text-gray-400">
            Timezone: {rules[0].timezone} (per-day windows share the rule&rsquo;s timezone)
          </div>
        )}
      </section>

      <section className="card p-4">
        <div className="section-label">Exceptions</div>
        <p className="mt-1 text-xs text-gray-500">
          Block a whole day (leave times empty) or a window on a specific date.
        </p>
        <ul className="mt-2 space-y-2">
          {exceptions.map((ex, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <input
                type="date"
                className="rounded-md border border-surface-border px-2 py-1 text-xs"
                value={ex.date}
                disabled={!editable}
                onChange={(e) =>
                  setExceptions((prev) =>
                    prev.map((v, j) => (j === i ? { ...v, date: e.target.value } : v)),
                  )
                }
              />
              <select
                className="rounded-md border border-surface-border px-2 py-1 text-xs"
                value={ex.kind}
                disabled={!editable}
                onChange={(e) =>
                  setExceptions((prev) =>
                    prev.map((v, j) =>
                      j === i ? { ...v, kind: e.target.value as 'unavailable' | 'extra' } : v,
                    ),
                  )
                }
              >
                <option value="unavailable">Unavailable</option>
                <option value="extra">Extra availability</option>
              </select>
              <input
                type="time"
                className="rounded-md border border-surface-border px-2 py-1 text-xs"
                value={ex.start_time ?? ''}
                disabled={!editable}
                onChange={(e) =>
                  setExceptions((prev) =>
                    prev.map((v, j) => (j === i ? { ...v, start_time: e.target.value || null } : v)),
                  )
                }
              />
              <span className="text-gray-400">–</span>
              <input
                type="time"
                className="rounded-md border border-surface-border px-2 py-1 text-xs"
                value={ex.end_time ?? ''}
                disabled={!editable}
                onChange={(e) =>
                  setExceptions((prev) =>
                    prev.map((v, j) => (j === i ? { ...v, end_time: e.target.value || null } : v)),
                  )
                }
              />
              {editable && (
                <button
                  type="button"
                  className="text-xs text-red-600 hover:underline"
                  onClick={() => setExceptions((prev) => prev.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
        {editable && (
          <button
            type="button"
            className="btn-secondary mt-2 !py-1 !text-xs"
            onClick={() =>
              setExceptions((prev) => [
                ...prev,
                { date: new Date().toISOString().slice(0, 10), kind: 'unavailable' },
              ])
            }
          >
            + Add exception
          </button>
        )}
      </section>

      {editable && (
        <div className="flex items-center gap-3">
          <button type="button" className="btn-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save availability'}
          </button>
          {savedAt && !saving && <span className="text-xs text-green-700">Saved ✓</span>}
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      )}

      <CalendarConnectionCard token={token} editable={editable} />
      <ZoomConnectionCard token={token} editable={editable} />
    </div>
  );
}

/** Bring-your-own-Zoom (Mediator OS Phase 1): mediators outside the
    platform Zoom account connect their own — their cases' sessions are
    then created and hosted on their Zoom, which is what makes live
    transcription and the sidebar work for them. Renders nothing until
    the engine has the Marketplace OAuth configured. */
function ZoomConnectionCard({ token, editable }: { token: string; editable: boolean }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'off' }
    | { kind: 'error' }
    | { kind: 'ready'; connection: { status: string; account_email: string | null } | null }
  >({ kind: 'loading' });
  const [busyAction, setBusyAction] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(() => {
    getZoomOwnConnection(token)
      .then((res) => setState(res.configured ? { kind: 'ready', connection: res.connection } : { kind: 'off' }))
      .catch(() => setState({ kind: 'error' }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (state.kind === 'loading' || state.kind === 'off') return null;
  if (state.kind === 'error') {
    return (
      <section className="card p-4">
        <div className="section-label">Your Zoom account</div>
        <p className="text-sm text-slate-600 mt-1">
          Couldn't load your Zoom connection status.{' '}
          <button type="button" className="underline" onClick={() => { setState({ kind: 'loading' }); load(); }}>Retry</button>
        </p>
      </section>
    );
  }
  const connection = state.connection;
  const connect = async () => {
    setBusyAction(true);
    try {
      const { url } = await startZoomOwnConnect(token);
      window.location.href = url;
    } catch (err) {
      setFlash(err instanceof EngineError ? err.message : 'Could not start the Zoom connection.');
      setBusyAction(false);
    }
  };
  const disconnect = async () => {
    setBusyAction(true);
    try {
      await disconnectZoomOwn(token);
      setConfirmDisconnect(false);
      setFlash('Zoom disconnected. Sessions will be hosted on the platform account again.');
      load();
    } catch {
      setFlash('Could not disconnect. Try again.');
    } finally {
      setBusyAction(false);
    }
  };

  return (
    <section className="card p-4">
      <div className="section-label">Your Zoom account</div>
      {flash && <div className="mt-1 text-xs text-gray-700">{flash}</div>}
      {!connection || connection.status === 'revoked' ? (
        <>
          <p className="mt-2 text-sm text-gray-600">
            Connect your own Zoom account and the sessions you mediate are created and hosted
            there — you're the host, and live transcription follows your meetings. Without a
            connection, sessions are hosted on the Levelheaded platform account.
          </p>
          {editable && (
            <button type="button" className="btn-primary mt-3" disabled={busyAction} onClick={() => void connect()}>
              {busyAction ? 'Opening Zoom…' : 'Connect your Zoom'}
            </button>
          )}
        </>
      ) : connection.status === 'error' ? (
        <>
          <p className="mt-2 text-sm text-amber-700">
            Your Zoom connection stopped working{connection.account_email ? ` (${connection.account_email})` : ''} —
            sessions are falling back to the platform account. Reconnect to fix it.
          </p>
          {editable && (
            <button type="button" className="btn-primary mt-3" disabled={busyAction} onClick={() => void connect()}>
              {busyAction ? 'Opening Zoom…' : 'Reconnect Zoom'}
            </button>
          )}
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-gray-700">
            ✓ Connected{connection.account_email ? <> as <strong>{connection.account_email}</strong></> : null}.
            New sessions you mediate are hosted on your Zoom account.
          </p>
          {editable && (confirmDisconnect ? (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="text-red-700">Disconnect? Future sessions fall back to the platform account.</span>
              <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setConfirmDisconnect(false)}>Keep</button>
              <button type="button" className="btn-primary !bg-red-600 !py-1 !text-xs" disabled={busyAction} onClick={() => void disconnect()}>Disconnect</button>
            </div>
          ) : (
            <button type="button" className="mt-2 text-xs text-gray-400 hover:text-red-700" onClick={() => setConfirmDisconnect(true)}>
              Disconnect…
            </button>
          ))}
        </>
      )}
    </section>
  );
}

/** Google Calendar connection: busy times sync in as scheduling
    exceptions; booked sessions push out. Renders nothing when the engine
    has calendar sync disabled. */
function CalendarConnectionCard({ token, editable }: { token: string; editable: boolean }) {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'off' }
    | { kind: 'error' }
    | { kind: 'ready'; connection: CalendarConnection | null }
  >({ kind: 'loading' });
  const [busyAction, setBusyAction] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(() => {
    getCalendarConnection(token)
      .then((res) => setState(res.configured ? { kind: 'ready', connection: res.connection } : { kind: 'off' }))
      // A fetch failure is NOT "feature off" — hiding the card on transient
      // errors made it look like calendar sync didn't exist (Rachel,
      // 2026-08-11). Show a retry row instead.
      .catch(() => setState({ kind: 'error' }));
  }, [token]);

  useEffect(() => {
    load();
    // OAuth return: the engine redirects back with ?calendar=connected|error.
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('calendar');
    if (flag) {
      setFlash(
        flag === 'connected'
          ? 'Google Calendar connected. Busy times now sync automatically.'
          : 'Calendar connection failed. Please try again.',
      );
      params.delete('calendar');
      const rest = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}`);
    }
  }, [load]);

  if (state.kind === 'loading' || state.kind === 'off') return null;
  if (state.kind === 'error') {
    return (
      <section className="card p-4">
        <div className="section-label">Connected calendar</div>
        <p className="text-sm text-slate-600 mt-1">
          Couldn't load your calendar connection status.{' '}
          <button type="button" className="underline" onClick={() => { setState({ kind: 'loading' }); load(); }}>
            Retry
          </button>
        </p>
      </section>
    );
  }
  const connection = state.connection;

  const connect = async () => {
    setBusyAction(true);
    try {
      const { auth_url } = await startCalendarConnect(token);
      window.location.href = auth_url;
    } catch (err) {
      setFlash(err instanceof EngineError ? err.message : 'Could not start the Google connection.');
      setBusyAction(false);
    }
  };

  const disconnect = async () => {
    setBusyAction(true);
    try {
      await disconnectCalendar(token);
      setConfirmDisconnect(false);
      setFlash('Calendar disconnected.');
      load();
    } catch {
      setFlash('Could not disconnect. Try again.');
    } finally {
      setBusyAction(false);
    }
  };

  const lastSync = connection?.last_synced_at
    ? `${Math.max(0, Math.round((Date.now() - new Date(connection.last_synced_at).getTime()) / 60000))} min ago`
    : 'not yet';

  return (
    <section className="card p-4">
      <div className="section-label">Connected calendar</div>
      {flash && <div className="mt-1 text-xs text-gray-700">{flash}</div>}
      {!connection && (
        <>
          <p className="mt-2 text-sm text-gray-600">
            Sync your Google Calendar so busy times are never offered to parties. Only free/busy
            times are read, never event details. Booked mediation sessions appear on your calendar
            with the Zoom link. Events marked &ldquo;Free&rdquo; in Google do not block scheduling.
          </p>
          {editable && (
            <button type="button" className="btn-primary mt-3" disabled={busyAction} onClick={() => void connect()}>
              {busyAction ? 'Opening Google…' : 'Connect Google Calendar'}
            </button>
          )}
        </>
      )}
      {connection && (
        <div className="mt-2 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">{connection.account_email ?? 'Google account'}</span>
            <span
              className={
                connection.status === 'active'
                  ? 'rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800'
                  : 'rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800'
              }
            >
              {connection.status === 'active' ? 'Syncing' : connection.status === 'error' ? 'Sync trouble' : 'Reconnect needed'}
            </span>
          </div>
          <div className="text-xs text-gray-500">Last synced: {lastSync}</div>
          <div className="text-xs text-gray-500">
            If we recently asked for updated permissions, hit Reconnect Google once so all of your
            calendars count toward your availability.
          </div>
          {connection.status !== 'active' && connection.last_sync_error && (
            <div className="text-xs text-amber-700">{connection.last_sync_error}</div>
          )}
          {editable && (
            <div className="flex items-center gap-3">
              {/* Always offer reconnect: re-running OAuth refreshes scopes
                  (e.g. the all-calendars permission) without disconnecting
                  first. Scott couldn't find a path to re-auth, 2026-08-13. */}
              <button type="button" className="btn-primary !py-1 !text-xs" disabled={busyAction} onClick={() => void connect()}>
                {connection.status === 'active' ? 'Reconnect Google' : 'Reconnect'}
              </button>
              {confirmDisconnect ? (
                <span className="flex items-center gap-2 text-xs">
                  Disconnect and stop syncing?
                  <button type="button" className="font-semibold text-red-700 hover:underline" disabled={busyAction} onClick={() => void disconnect()}>
                    Yes, disconnect
                  </button>
                  <button type="button" className="hover:underline" onClick={() => setConfirmDisconnect(false)}>
                    Keep
                  </button>
                </span>
              ) : (
                <button type="button" className="text-xs text-gray-500 hover:text-red-700 hover:underline" onClick={() => setConfirmDisconnect(true)}>
                  Disconnect
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
